/**
 * Updates collection cards to in-stock counts (filter.v.availability=1)
 * and removes collections with nothing available.
 */
(function () {
  if (window.__psInStockCountsInit) return;
  window.__psInStockCountsInit = true;

  const CACHE_KEY = 'ps-in-stock-counts';
  const CACHE_TTL_MS = 15 * 60 * 1000;
  const FETCH_TIMEOUT_MS = 8000;
  const CONCURRENCY = 4;
  const MAX_SCAN_BYTES = 250000;
  const UPDATED_EVENT = 'ps:in-stock-counts:updated';
  const META_RE = /name=["']ps-in-stock-count["'][^>]*content=["'](\d+)["']/i;
  const inflight = new Map();

  function loadCache() {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '');
      if (!parsed?.counts || typeof parsed.counts !== 'object') return {};
      if (Date.now() - Number(parsed.ts) > CACHE_TTL_MS) {
        sessionStorage.removeItem(CACHE_KEY);
        return {};
      }
      return parsed.counts;
    } catch (e) {
      return {};
    }
  }

  const counts = loadCache();

  function saveCount(handle, count) {
    counts[handle] = count;
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), counts }));
    } catch (e) {
      /* private mode / quota */
    }
  }

  function parseCount(html) {
    const match = String(html).match(META_RE);
    const count = match ? parseInt(match[1], 10) : NaN;
    return Number.isFinite(count) ? count : null;
  }

  function filteredCollectionUrl(collectionUrl) {
    const url = new URL(collectionUrl, window.location.origin);
    url.searchParams.delete('filter.v.availability');
    url.searchParams.set('filter.v.availability', '1');

    const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewId) url.searchParams.set('preview_theme_id', previewId);

    return url;
  }

  async function readCount(response, signal) {
    if (!response.body?.getReader) return parseCount(await response.text());

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    try {
      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;

        buf += decoder.decode(value, { stream: true });
        const count = parseCount(buf);
        if (count != null) return count;
        if (buf.length >= MAX_SCAN_BYTES) break;
      }
    } finally {
      try {
        await reader.cancel();
      } catch (e) {
        /* already closed */
      }
    }

    return parseCount(buf);
  }

  function fetchCount(collectionUrl) {
    const existing = inflight.get(collectionUrl);
    if (existing) return existing;

    const request = (async () => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

      try {
        const response = await fetch(filteredCollectionUrl(collectionUrl), {
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) return null;
        return await readCount(response, controller.signal);
      } catch (e) {
        return null;
      } finally {
        window.clearTimeout(timer);
      }
    })();

    inflight.set(collectionUrl, request);
    return request;
  }

  function markReady(card) {
    card.setAttribute('data-count-ready', '');
    card.querySelector('[data-in-stock-label]')?.setAttribute('data-count-ready', '');
  }

  function settleCard(card, count) {
    if (count === 0) {
      card.remove();
      return;
    }

    const label = card.querySelector('[data-in-stock-label]');
    if (label) {
      const singular = card.getAttribute('data-count-singular') || 'item';
      const plural = card.getAttribute('data-count-plural') || 'items';
      label.textContent = `${count} ${count === 1 ? singular : plural}`;
    }

    markReady(card);
  }

  function notify() {
    document.dispatchEvent(new CustomEvent(UPDATED_EVENT, { bubbles: true }));
  }

  async function refresh(root) {
    const cards = Array.from((root || document).querySelectorAll('[data-in-stock-card]')).filter(
      (card) => card instanceof HTMLElement
    );
    if (!cards.length) return;

    const pending = [];

    for (const card of cards) {
      const handle = card.getAttribute('data-collection-handle') || '';
      if (Object.prototype.hasOwnProperty.call(counts, handle)) {
        settleCard(card, counts[handle]);
      } else {
        pending.push(card);
      }
    }

    if (pending.length < cards.length) notify();

    let index = 0;
    async function worker() {
      while (index < pending.length) {
        const card = pending[index++];
        if (!card.isConnected) continue;

        const url = card.getAttribute('data-collection-url');
        if (!url) continue;

        const count = await fetchCount(url);
        if (!card.isConnected) continue;

        if (count == null) {
          markReady(card);
          continue;
        }

        const handle = card.getAttribute('data-collection-handle');
        if (handle) saveCount(handle, count);
        settleCard(card, count);
      }
    }

    if (pending.length) {
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, pending.length) }, () => worker())
      );
    }

    notify();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(document));
  } else {
    refresh(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) refresh(event.target);
  });
})();
